import React, { useState } from 'react';
import { Box, Button, Paper, Divider, Dialog, DialogTitle, DialogContent, Typography, DialogActions } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Delete } from '@mui/icons-material';
import SingleSkillDropdown from './SingleSkillDropdown';
import ExperienceField from './ExperienceField';
import PanIndiaDropdown from './PanIndiaDropdown';
import StateDropdown from './StateDropdown';
import SkillDescriptionField from './SkillDescriptionField';
import { SkillRowProps } from '../../types/profileForm.types';
import ColorData from 'Components/CstmTheme/ColorData';

const SkillRow: React.FC<SkillRowProps> = ({
  skillEntry,
  errors,
  canRemove,
  onChange,
  onRemove,
  onBlur,
}) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleSkillChange = (field: string, value: string) => {
    onChange({ skillName: value });
  };

  const handleExperienceChange = (value: number) => {
    onChange({ experienceYears: value });
  };

  const handlePanIndiaChange = (value: boolean) => {
    onChange({
      panIndia: value,
      selectedStates: value ? [] : skillEntry.selectedStates
    });
  };

  const handleStateChange = (field: string, value: string) => {
    // For single state selection, replace the array
    onChange({ selectedStates: value ? [value] : [] });
  };

  const handleDescriptionChange = (value: string) => {
    onChange({ description: value });
  };

  const handleRemove = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmRemove = () => {
    setOpenConfirmDialog(false);
    onRemove();
  };

  const handleCancelRemove = () => {
    setOpenConfirmDialog(false);
  };

  const handleBlur = (field: string) => {
    onBlur(`skillEntries.${skillEntry.id}.${field}`);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        position: 'relative'
      }}
    >
      <Grid container spacing={2}>
        {/* First Row: Skill Selection, Experience, and Remove Button */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <SingleSkillDropdown
            id={skillEntry.id}
            label="Skill"
            value={skillEntry.skillName}
            error={errors?.skillName}
            required
            onChange={handleSkillChange}
            onBlur={() => handleBlur('skillName')}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <ExperienceField
            label="Experience (Years)"
            value={skillEntry.experienceYears}
            error={errors?.experienceYears}
            onChange={handleExperienceChange}
            onBlur={() => handleBlur('experienceYears')}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '48px' }}>
            {canRemove && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Delete />}
                onClick={handleRemove}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  borderColor: '#dc3545',
                  color: '#dc3545',
                  '&:hover': {
                    backgroundColor: '#f8d7da',
                    borderColor: '#dc3545',
                  },
                }}
              >
                Remove
              </Button>
            )}
          </Box>
        </Grid>

        {/* Second Row: Pan India and State Selection */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <PanIndiaDropdown
            label="Pan India"
            value={skillEntry.panIndia}
            error={errors?.selectedStates}
            onChange={handlePanIndiaChange}
            onBlur={() => handleBlur('panIndia')}
          />
        </Grid>

        {!skillEntry.panIndia && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <StateDropdown
              label="Select State"
              value={skillEntry.selectedStates[0] || ''}
              error={errors?.selectedStates}
              onChange={handleStateChange}
              onBlur={() => handleBlur('selectedStates')}
            />
          </Grid>
        )}

        {/* Third Row: Description */}
        <Grid size={{ xs: 12 }}>
          <SkillDescriptionField
            label="Description"
            value={skillEntry.description}
            error={errors?.description}
            onChange={handleDescriptionChange}
            onBlur={() => handleBlur('description')}
            maxLength={500}
          />
        </Grid>
      </Grid>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelRemove}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
          Remove Skill
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove this skill?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRemove}
            sx={{ minWidth: 90, mr: 1, backgroundColor: ColorData.primary.main, '&:hover': { backgroundColor: ColorData.hover.main } }}
          >
            Confirm
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancelRemove}
            sx={{ minWidth: 90 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

export default SkillRow;